import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { 
  signInWithEmailAndPassword, 
  signOut,
  User,
  sendPasswordResetEmail
} from 'firebase/auth';
import { auth } from '../config/firebase';
import { getIdToken, getIdTokenForced, storeToken, clearStoredToken, getStoredToken, TokenInfo } from '../utils/tokenUtils';
import { config } from '../config';
import { TermsStatus } from '../types/terms';

interface AuthState {
  user: User | null;
  loading: boolean;
  error: string | null;
  isAuthenticated: boolean;
  token: string | null;
  tokenInfo: TokenInfo | null;
  tokenLoading: boolean;
  isMongoVerified: boolean;
  mongoUser: any | null;
  isAdmin: boolean;
  orgId: string | null;
  // Absent on older backends — null means "no gate applies".
  termsStatus: TermsStatus | null;
}

const initialState: AuthState = {
  user: null,
  loading: false,
  error: null,
  isAuthenticated: false,
  token: null,
  tokenInfo: null,
  tokenLoading: false,
  isMongoVerified: false,
  mongoUser: null,
  isAdmin: false,
  orgId: null,
  termsStatus: null
};

// Login user
export const loginUser = createAsyncThunk(
  'auth/loginUser',
  async ({ email, password }: { email: string; password: string }, { rejectWithValue }) => {
    try {
      console.debug('[AuthSlice] Starting login process for:', email);
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      
      // Get JWT token after successful login
      console.debug('[AuthSlice] Login successful, getting JWT token...');
      const tokenInfo = await getIdToken();
      
      if (tokenInfo) {
        storeToken(tokenInfo);
        console.debug('[AuthSlice] JWT token obtained and stored successfully');
        
        // All users need MongoDB verification now
        // Role-based access is determined by the backend
        
        // For non-admin users, verify MongoDB user via Node.js backend
        try {
          const apiUrl = config.server.backendUrl || 'http://localhost:8000';
          const response = await fetch(`${apiUrl}/api/admin/verify-access`, {
            headers: {
              'Authorization': `Bearer ${tokenInfo.token}`
            }
          });

          console.log('veification check repsnsen is', response)

          if (response.ok) {
            const data = await response.json();
            console.debug('[AuthSlice] MongoDB user verified:', data.data.user.email);
            console.debug('[AuthSlice] Organization ID:', data.data.org_id);
            return {
              user: userCredential.user,
              tokenInfo,
              mongoUser: data.data.user,
              isAdmin: false,
              orgId: data.data.org_id,
              termsStatus: data.data?.termsStatus ?? null
            };
          } else {
            // Parse error response for better error messages
            const errorData = await response.json();
            const errorCode = errorData.error || 'USER_NOT_AUTHORIZED';
            const errorMessage = errorData.message || 'User not authorized for access';

            console.warn('[AuthSlice] MongoDB verification failed:', errorCode);

            // Map error codes to user-friendly messages
            let userMessage = errorMessage;
            switch (errorCode) {
              case 'NO_SUBSCRIPTION':
                userMessage = 'No active subscription found. Please contact your administrator to create a subscription.';
                break;
              case 'ORGANIZATION_NOT_FOUND':
                userMessage = 'Organization not found. Please contact your administrator to set up your account.';
                break;
              case 'SUBSCRIPTION_EXPIRED':
                userMessage = 'Your subscription has expired. Please contact your administrator to renew the subscription.';
                break;
              case 'SUBSCRIPTION_GRACE_PERIOD_ENDED':
                userMessage = 'Your subscription grace period has ended. Please contact your administrator to update the payment method.';
                break;
              case 'USER_DISABLED':
                userMessage = 'Your access has been revoked by the administrator. If you think it\'s a mistake, please contact support.';
                break;
              case 'USER_NOT_REGISTERED':
                userMessage = 'Your account is not registered in the system. Please contact your administrator.';
                break;
              case 'USER_NOT_AUTHORIZED':
                userMessage = 'You are not authorized to access this organization. Please contact your administrator to request access.';
                break;
              case 'AUTH_CHECK_FAILED':
                userMessage = 'Unable to verify authorization. Please try again later.';
                break;
              default:
                userMessage = errorMessage;
            }

            throw new Error(userMessage);
          }
        } catch (mongoError: any) {
          console.error('[AuthSlice] MongoDB verification error:', mongoError);
          throw new Error(mongoError.message || 'MongoDB verification failed');
        }
      } else {
        console.warn('[AuthSlice] Failed to get JWT token after login');
        throw new Error('Failed to get authentication token');
      }
    } catch (error: any) {
      console.error('[AuthSlice] Login failed:', error);

      // Map Firebase error codes to user-friendly messages
      let errorMessage = error.message;

      if (error.code === 'auth/user-disabled') {
        errorMessage = 'Your access has been revoked by the administrator. If you think it\'s a mistake, please contact support.';
      } else if (error.code === 'auth/wrong-password' || error.code === 'auth/invalid-credential') {
        errorMessage = 'Invalid email or password. Please try again.';
      } else if (error.code === 'auth/user-not-found') {
        errorMessage = 'No account found with this email address.';
      } else if (error.code === 'auth/too-many-requests') {
        errorMessage = 'Too many failed login attempts. Please try again later.';
      }

      return rejectWithValue(errorMessage);
    }
  }
);

// Logout user
export const logoutUser = createAsyncThunk(
  'auth/logoutUser',
  async (_, { rejectWithValue }) => {
    try {
      console.debug('[AuthSlice] Starting logout process...');
      await signOut(auth);
      clearStoredToken();
      console.debug('[AuthSlice] Logout successful, tokens cleared');
      return null;
    } catch (error: any) {
      console.error('[AuthSlice] Logout failed:', error);
      // Still clear tokens even if Firebase signOut fails
      clearStoredToken();
      return rejectWithValue(error.message);
    }
  }
);

// Refresh token
export const refreshToken = createAsyncThunk(
  'auth/refreshToken',
  async (_, { rejectWithValue }) => {
    try {
      console.debug('[AuthSlice] Refreshing JWT token...');
      const tokenInfo = await getIdTokenForced();
      
      if (tokenInfo) {
        storeToken(tokenInfo);
        console.debug('[AuthSlice] Token refreshed successfully');
        return tokenInfo;
      } else {
        console.warn('[AuthSlice] Failed to refresh token');
        return rejectWithValue('Failed to refresh token');
      }
    } catch (error: any) {
      console.error('[AuthSlice] Token refresh failed:', error);
      return rejectWithValue(error.message);
    }
  }
);

// Verify MongoDB user
export const verifyMongoUser = createAsyncThunk(
  'auth/verifyMongoUser',
  async (token: string, { rejectWithValue }) => {
    try {
      const response = await fetch(`${config.server.backendUrl || 'http://localhost:8000'}/api/admin/verify-access`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const error = await response.json();
        const errorCode = error.error;

        // Map specific error codes to user-friendly messages
        let userMessage = error.message || 'User verification failed';
        switch (errorCode) {
          case 'USER_NOT_AUTHORIZED':
            userMessage = 'You are not authorized to access this organization. Please contact your administrator to request access.';
            break;
          case 'AUTH_CHECK_FAILED':
            userMessage = 'Unable to verify authorization. Please try again later.';
            break;
          case 'USER_NOT_REGISTERED':
            userMessage = 'Your account is not registered in the system. Please contact your administrator.';
            break;
          case 'USER_DISABLED':
            userMessage = 'Your access has been revoked by the administrator. If you think it\'s a mistake, please contact support.';
            break;
          case 'NO_SUBSCRIPTION':
            userMessage = 'No active subscription found. Please contact your administrator.';
            break;
          case 'SUBSCRIPTION_EXPIRED':
            userMessage = 'Your subscription has expired. Please contact your administrator.';
            break;
        }

        throw new Error(userMessage);
      }

      const data = await response.json();
      return {
        user: data.data?.user ?? null,
        termsStatus: data.data?.termsStatus ?? null
      };
    } catch (error: any) {
      console.error('[AuthSlice] MongoDB verification failed:', error);
      return rejectWithValue(error.message);
    }
  }
);

// Send password reset email
export const sendResetEmail = createAsyncThunk(
  'auth/sendResetEmail',
  async (email: string, { rejectWithValue }) => {
    try {
      await sendPasswordResetEmail(auth, email);
      return { success: true };
    } catch (error: any) {
      console.error('[AuthSlice] Password reset failed:', error);
      return rejectWithValue(error.message);
    }
  }
);

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setUser: (state, action: PayloadAction<User | null>) => {
      console.debug('[AuthSlice] Setting user:', action.payload?.email || 'null');
      state.user = action.payload;
      
      // Only set isAuthenticated if we have both user AND valid token
      if (action.payload && state.tokenInfo) {
        // Check if token is not expired
        const now = Date.now();
        const bufferTime = 5 * 60 * 1000; // 5 minutes buffer
        const tokenValid = now < (state.tokenInfo.expiresAt - bufferTime);
        
        // Admin status and MongoDB verification will be determined by backend
        // Role-based access is now handled through the database

        state.isAuthenticated = tokenValid && state.isMongoVerified;
        
        if (!tokenValid) {
          console.warn('[AuthSlice] Token expired, user not authenticated');
          state.token = null;
          state.tokenInfo = null;
          clearStoredToken();
        }
      } else {
        state.isAuthenticated = false;
      }
      
      // If user is null, clear everything
      if (!action.payload) {
        state.token = null;
        state.tokenInfo = null;
        state.isAuthenticated = false;
        state.isMongoVerified = false;
        state.mongoUser = null;
        state.isAdmin = false;
        state.termsStatus = null;
        clearStoredToken();
      }
    },
    clearError: (state) => {
      state.error = null;
    },
    setError: (state, action: PayloadAction<string>) => {
      state.error = action.payload;
    },
    setToken: (state, action: PayloadAction<TokenInfo | null>) => {
      if (action.payload) {
        console.debug('[AuthSlice] Setting token in state:', {
          expiresAt: new Date(action.payload.expiresAt).toISOString(),
          tokenLength: action.payload.token.length
        });
        state.token = action.payload.token;
        state.tokenInfo = action.payload;
      } else {
        console.debug('[AuthSlice] Clearing token from state');
        state.token = null;
        state.tokenInfo = null;
      }
    },
    initializeStoredToken: (state) => {
      // Only initialize if we don't already have a token in state
      if (state.token || state.tokenInfo) {
        console.debug('[AuthSlice] Token already in state, skipping initialization');
        return;
      }
      
      const storedToken = getStoredToken();
      if (storedToken) {
        console.debug('[AuthSlice] Initializing stored token:', {
          expiresAt: new Date(storedToken.expiresAt).toISOString(),
          tokenLength: storedToken.token.length
        });
        state.token = storedToken.token;
        state.tokenInfo = storedToken;
      } else {
        console.debug('[AuthSlice] No valid stored token found');
      }
    },
    validateAuth: (state) => {
      // Validate current authentication state
      // Admin users only need user and token, regular users need MongoDB verification
      const needsMongoVerification = !state.isAdmin && !state.isMongoVerified;
      
      if (!state.user || !state.tokenInfo || needsMongoVerification) {
        console.debug('[AuthSlice] Missing requirements for auth:', {
          hasUser: !!state.user,
          hasToken: !!state.tokenInfo,
          isAdmin: state.isAdmin,
          isMongoVerified: state.isMongoVerified,
          needsMongoVerification
        });
        state.isAuthenticated = false;
        return;
      }
      
      // Check if token is expired
      const now = Date.now();
      const bufferTime = 5 * 60 * 1000; // 5 minutes buffer
      const tokenValid = now < (state.tokenInfo.expiresAt - bufferTime);
      
      if (!tokenValid) {
        console.warn('[AuthSlice] Token expired during validation');
        state.isAuthenticated = false;
        state.token = null;
        state.tokenInfo = null;
        clearStoredToken();
      } else {
        state.isAuthenticated = true;
      }
    },
    setTermsStatus: (state, action: PayloadAction<TermsStatus | null>) => {
      state.termsStatus = action.payload;
    },
    // The user accepted the EUTC via POST /api/auth/accept-eutc — clear the
    // gate locally so the app unblocks without a reload.
    markEutcAccepted: (state) => {
      if (state.termsStatus) {
        state.termsStatus.eutcAccepted = true;
      }
    },
    // The user completed the forced password change — clear the gate locally.
    clearMustChangePassword: (state) => {
      if (state.termsStatus) {
        state.termsStatus.mustChangePassword = false;
      }
    },
    setMongoVerified: (state, action: PayloadAction<boolean>) => {
      console.debug('[AuthSlice] Setting MongoDB verification status:', action.payload);
      state.isMongoVerified = action.payload;
      
      // Update authentication status if we now have all requirements
      if (state.user && state.tokenInfo && action.payload) {
        const now = Date.now();
        const bufferTime = 5 * 60 * 1000;
        const tokenValid = now < (state.tokenInfo.expiresAt - bufferTime);
        state.isAuthenticated = tokenValid;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      // Login
      .addCase(loginUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload.user;
        state.isAuthenticated = true;
        state.error = null;
        state.isAdmin = action.payload.isAdmin;
        state.orgId = action.payload.orgId || null;
        state.termsStatus = action.payload?.termsStatus ?? null;

        if (action.payload.isAdmin) {
          // Admin users bypass MongoDB verification
          state.isMongoVerified = true;
          state.mongoUser = null;
        } else {
          // Regular users need MongoDB verification
          state.isMongoVerified = true;
          state.mongoUser = action.payload.mongoUser;
        }

        if (action.payload.tokenInfo) {
          state.token = action.payload.tokenInfo.token;
          state.tokenInfo = action.payload.tokenInfo;
        }
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        state.isAuthenticated = false;
        state.isMongoVerified = false;
      })
      // Logout
      .addCase(logoutUser.fulfilled, (state) => {
        state.user = null;
        state.isAuthenticated = false;
        state.loading = false;
        state.error = null;
        state.token = null;
        state.tokenInfo = null;
        state.isMongoVerified = false;
        state.mongoUser = null;
        state.isAdmin = false;
        state.orgId = null;
        state.termsStatus = null;
      })
      // Token refresh
      .addCase(refreshToken.pending, (state) => {
        state.tokenLoading = true;
      })
      .addCase(refreshToken.fulfilled, (state, action) => {
        state.tokenLoading = false;
        state.token = action.payload.token;
        state.tokenInfo = action.payload;
      })
      .addCase(refreshToken.rejected, (state) => {
        state.tokenLoading = false;
      })
      // MongoDB verification
      .addCase(verifyMongoUser.fulfilled, (state, action) => {
        state.isMongoVerified = true;
        state.mongoUser = action.payload?.user ?? null;
        state.termsStatus = action.payload?.termsStatus ?? null;
        // Check if we can now authenticate
        if (state.user && state.tokenInfo) {
          state.isAuthenticated = true;
        }
      })
      .addCase(verifyMongoUser.rejected, (state) => {
        state.isMongoVerified = false;
        state.mongoUser = null;
        state.isAuthenticated = false;
      });
  },
});

export const {
  setUser,
  clearError,
  setError,
  setToken,
  initializeStoredToken,
  validateAuth,
  setMongoVerified,
  setTermsStatus,
  markEutcAccepted,
  clearMustChangePassword
} = authSlice.actions;

export default authSlice.reducer;